<script setup lang="ts">
/**
 * Политика в отношении обработки персональных данных.
 *
 * Третий правовой документ рядом с офертой и правилами приёма платежей, и
 * появился он не для галочки в чеклисте амоМаркета. У сервиса три потока чужих
 * персональных данных, и о каждом клиент вправе знать до того, как нажмёт
 * «Разрешить»: его собственные данные и данные его сотрудников; данные третьих
 * лиц внутри материалов, сделок CRM и заявок с опубликованных документов; и
 * содержимое, которое уезжает к поставщику моделей за границу.
 *
 * Третье — единственная трансграничная передача в сервисе, и написать о ней
 * прямо дешевле, чем объясняться потом: документ, из которого клиент узнаёт про
 * неё сам, лучше документа, из-за которого он узнаёт про неё от проверяющего.
 *
 * Реквизиты берутся из того же публичного ответа, что в оферте: два места, где
 * ИНН набран руками, разъезжаются на первой же правке.
 */
import { computed } from 'vue';
import LegalDocument from '../../components/legal/LegalDocument.vue';
import { legal } from '../../site/public-data.js';

const company = computed(() => legal.value?.company ?? null);
/**
 * Имя оператора одной строкой.
 *
 * Запасной вариант — не «Оператор», а описательный оборот. Обычно реквизиты
 * уже в разметке (их кладёт пререндер), но если сборка шла без доступа к API,
 * их дорисует запрос — и до ответа документ не должен читаться как «данных
 * Оператор (далее — Оператор)».
 */
const operator = computed(
  () => company.value?.fullName || company.value?.name || 'оператор сервиса Keypi',
);
</script>

<template>
  <LegalDocument
    title="Политика обработки персональных данных"
    revision="24 августа 2026 г."
    subtitle="Какие персональные данные обрабатывает сервис Keypi, зачем, кому передаёт и как их
      удалить. Документ составлен по требованиям Федерального закона от 27.07.2006 № 152-ФЗ
      «О персональных данных»."
  >
    <h2 id="general">1. Общие положения</h2>
    <p>
      1.1. Настоящая Политика определяет порядок, в котором {{ operator }} (далее — Оператор)
      обрабатывает персональные данные при работе сервиса Keypi (далее — Сервис), доступного по
      адресу keypi.ru, и выполняет требование части 2 статьи 18.1 Федерального закона от 27.07.2006
      № 152-ФЗ «О персональных данных».
    </p>
    <p>
      1.2. Политика распространяется на все персональные данные, которые Оператор получает при
      использовании Сервиса, включая данные, поступающие из подключённых Заказчиком внешних систем.
    </p>
    <p>
      1.3. Термины «персональные данные», «обработка», «оператор», «субъект персональных данных»
      используются в значениях, установленных статьёй 3 Закона № 152-ФЗ.
    </p>
    <p>
      1.4. Действующая редакция Политики публикуется на этой странице и доступна без регистрации.
      Условия оказания услуг определяются <router-link to="/offer">публичной офертой</router-link>,
      порядок расчётов — <router-link to="/payment-rules">Правилами приёма платежей</router-link>.
    </p>

    <h2 id="subjects">2. Чьи данные обрабатываются</h2>
    <p>2.1. Оператор обрабатывает персональные данные следующих категорий субъектов:</p>
    <ul>
      <li>
        <strong>Пользователи Сервиса</strong> — лица, создавшие учётную запись или приглашённые в
        организацию Заказчика;
      </li>
      <li>
        <strong>Представители Заказчика</strong> — лица, указанные в реквизитах организации и в
        документах об оплате;
      </li>
      <li>
        <strong>Третьи лица</strong>, чьи данные содержатся в материалах Заказчика, в сделках
        подключённой CRM и в заявках, поступивших с опубликованных документов Заказчика.
      </li>
    </ul>
    <p>
      2.2. В отношении третьих лиц (пункт 2.1, третий абзац) Оператор действует по поручению
      Заказчика — порядок такой обработки описан в разделе 7.
    </p>

    <h2 id="data">3. Какие данные обрабатываются</h2>
    <p>3.1. <strong>При создании учётной записи и работе в Сервисе:</strong></p>
    <ul>
      <li>адрес электронной почты и имя пользователя;</li>
      <li>
        пароль — только в виде необратимого криптографического хеша; исходный пароль Оператору
        неизвестен и им не хранится;
      </li>
      <li>роль в организации, факт и время подтверждения адреса электронной почты;</li>
      <li>
        сведения об организации Заказчика: наименование, ИНН, КПП, адрес — они необходимы для
        выставления счетов и закрывающих документов.
      </li>
    </ul>
    <p>3.2. <strong>При оплате:</strong></p>
    <ul>
      <li>
        сведения о платежах: сумма, дата, номер счёта, способ оплаты, статус, а также реквизиты
        плательщика — юридического лица;
      </li>
      <li>
        идентификатор привязки банковской карты, выданный банком-эквайером, — для повторных
        списаний по подписке.
      </li>
    </ul>
    <p>
      3.3. <strong>Реквизиты банковских карт Оператору не передаются и им не хранятся.</strong>
      Номер карты, срок действия и код проверки вводятся на защищённой странице банка-эквайера и
      Сервису недоступны.
    </p>
    <p>3.4. <strong>Содержимое, которое создаёт и загружает Заказчик:</strong></p>
    <ul>
      <li>тексты задач, брифы и сообщения чата с агентом;</li>
      <li>
        загруженные файлы и аудиозаписи, а также результат распознавания речи из этих
        аудиозаписей;
      </li>
      <li>подготовленные документы, их версии и ссылки на них.</li>
    </ul>
    <p>3.5. <strong>При подключении внешней CRM:</strong></p>
    <ul>
      <li>
        данные сделок, компаний и контактов, необходимые для сборки документа: наименование,
        бюджет, имя и контактные данные представителя, вложенные к сделке файлы;
      </li>
      <li>
        маркеры доступа к аккаунту CRM — только в зашифрованном виде, и сведения о самом
        подключении: идентификатор и адрес аккаунта, состояние связи.
      </li>
    </ul>
    <p>
      3.6. <strong>Заявки с опубликованных документов</strong> — имя, телефон, адрес электронной
      почты и сообщение, которые посетитель оставил в форме документа Заказчика.
    </p>
    <p>3.7. <strong>Технические данные:</strong></p>
    <ul>
      <li>IP-адрес, сведения о браузере и устройстве, дата и время обращений;</li>
      <li>
        записи журналов работы Сервиса, необходимые для разбора сбоев и выявления попыток
        неправомерного доступа;
      </li>
      <li>файлы cookie — их состав и назначение описаны в разделе 10;</li>
      <li>
        метки рекламной кампании (utm-метки, идентификаторы клика рекламных систем), адрес
        страницы входа и доменное имя сайта, с которого совершён переход, — если Пользователь
        пришёл по рекламной или партнёрской ссылке. Полный адрес перехода не сохраняется.
      </li>
    </ul>
    <p>
      3.8. Специальные категории персональных данных (статья 10 Закона № 152-ФЗ) и биометрические
      персональные данные (статья 11) Оператор не обрабатывает и не запрашивает. Заказчику не
      следует загружать такие данные в Сервис.
    </p>

    <h2 id="purposes">4. Цели обработки</h2>
    <p>4.1. Персональные данные обрабатываются для следующих целей:</p>
    <ul>
      <li>заключение и исполнение договора, оказание услуг Сервиса;</li>
      <li>
        подготовка документов по заданию Заказчика — основная функция Сервиса, ради которой
        содержимое из раздела 3 и обрабатывается;
      </li>
      <li>расчёты, выставление счетов и оформление закрывающих документов;</li>
      <li>
        связь с пользователем: подтверждение адреса почты, восстановление доступа, уведомления о
        готовности документов, о состоянии баланса и подписки;
      </li>
      <li>техническая поддержка и разбор обращений;</li>
      <li>обеспечение безопасности, выявление и пресечение неправомерного доступа;</li>
      <li>исполнение обязанностей, возложенных на Оператора законодательством.</li>
    </ul>
    <p>
      4.2. Для рекламных рассылок и профилирования персональные данные не используются. Для
      обучения моделей искусственного интеллекта материалы Заказчика Оператором не передаются.
    </p>

    <h2 id="grounds">5. Правовые основания</h2>
    <p>5.1. Обработка ведётся на следующих основаниях:</p>
    <ul>
      <li>
        договор, заключаемый присоединением к <router-link to="/offer">оферте</router-link>, —
        обработка необходима для его исполнения (пункт 5 части 1 статьи 6 Закона № 152-ФЗ);
      </li>
      <li>
        согласие субъекта — для действий, выходящих за рамки исполнения договора, в том числе для
        трансграничной передачи по разделу 8;
      </li>
      <li>
        поручение Заказчика — для данных третьих лиц, поступающих в составе его материалов, сделок
        CRM и заявок (часть 3 статьи 6 Закона № 152-ФЗ);
      </li>
      <li>
        требования законодательства о бухгалтерском учёте, налогах и противодействии легализации
        доходов — для сведений о платежах.
      </li>
    </ul>

    <h2 id="how">6. Как обрабатываются данные</h2>
    <p>
      6.1. Обработка ведётся смешанным способом: автоматизированно и без использования средств
      автоматизации. Действия — сбор, запись, систематизация, накопление, хранение, уточнение,
      извлечение, использование, передача в объёме раздела 8, блокирование, удаление и уничтожение.
    </p>
    <p>
      6.2. Базы данных Сервиса и хранилище файлов размещаются на серверах на территории Российской
      Федерации (Yandex Cloud, регион ru-central1). Запись, систематизация, накопление, хранение,
      уточнение и извлечение персональных данных граждан Российской Федерации ведутся с
      использованием этих баз (часть 5 статьи 18 Закона № 152-ФЗ).
    </p>
    <p>
      6.3. Решения, порождающие юридические последствия в отношении субъекта, исключительно на
      основании автоматизированной обработки Оператором не принимаются.
    </p>

    <h2 id="processor">7. Обработка по поручению Заказчика</h2>
    <p>
      7.1. Материалы Заказчика, данные его сделок в подключённой CRM и заявки с его документов
      могут содержать персональные данные третьих лиц. В отношении таких данных
      <strong>оператором является Заказчик</strong>, а Сервис обрабатывает их по его поручению и
      только для подготовки документов и передачи заявок самому Заказчику.
    </p>
    <p>
      7.2. Заказчик обязан обеспечить наличие правового основания для передачи Оператору
      персональных данных третьих лиц и, если требуется, получить их согласие. Это же условие
      установлено разделом 8 <router-link to="/offer">оферты</router-link>, в перечне обязанностей
      Заказчика.
    </p>
    <p>
      7.3. Оператор не определяет состав и цели обработки таких данных, не использует их для
      собственных целей и не передаёт третьим лицам за пределами раздела 8.
    </p>
    <p>
      7.4. Состав данных, которые Сервис читает в подключённой CRM, ограничен тем, что необходимо
      для сборки документа. Доступ выдаёт администратор аккаунта CRM, и он же вправе отозвать его в
      любой момент — отключением интеграции на своей стороне или в личном кабинете Keypi.
    </p>

    <h2 id="transfer">8. Передача третьим лицам</h2>
    <p>
      8.1. Оператор не продаёт персональные данные и не передаёт их третьим лицам, кроме случаев,
      перечисленных ниже, и случаев, предусмотренных законом.
    </p>
    <p>8.2. По состоянию на дату редакции привлечены следующие обработчики:</p>
    <ul>
      <li>
        <strong>Yandex Cloud</strong> — размещение серверов и баз данных Сервиса, хранение файлов,
        доставка электронной почты и распознавание речи из загруженных аудиозаписей. Российская
        Федерация, регион ru-central1.
      </li>
      <li>
        <strong>Банк-эквайер</strong> — приём платежей и повторные списания по подписке;
        реквизиты карт обрабатывает на своей стороне и Оператору не передаёт. Российская Федерация.
      </li>
      <li>
        <strong>Поставщик доступа к моделям искусственного интеллекта</strong> — подготовка
        документа по заданию Заказчика. Соединённые Штаты Америки; см. пункт 8.3.
      </li>
    </ul>
    <p>
      8.3. <strong>Трансграничная передача.</strong> Тексты заданий, брифов, сообщений чата и
      данные, из которых собирается документ, — включая сведения из подключённой CRM, — передаются
      поставщику доступа к моделям искусственного интеллекта, расположенному в США. Если такие
      материалы содержат персональные данные, они передаются на территорию иностранного
      государства, не обеспечивающего адекватной защиты прав субъектов персональных данных.
      Передача осуществляется в объёме, необходимом для выполнения задания, и с согласия субъекта
      либо по поручению Заказчика при наличии у него правового основания. Это единственная
      трансграничная передача в Сервисе.
    </p>
    <p>
      8.4. Обработчики привлекаются на основании договоров, обязывающих их соблюдать
      конфиденциальность и требования к защите персональных данных. Ответственность перед субъектом
      за действия обработчика несёт Оператор.
    </p>
    <p>
      8.5. Данные могут быть предоставлены государственным органам по мотивированному запросу в
      порядке, установленном законом.
    </p>

    <h2 id="retention">9. Сроки хранения</h2>
    <p>9.1. Данные хранятся не дольше, чем этого требуют цели обработки:</p>
    <ul>
      <li>
        <strong>Учётная запись и организация</strong> — в течение действия договора и трёх лет
        после его прекращения (общий срок исковой давности), если законом не установлен больший
        срок;
      </li>
      <li>
        <strong>Документы, материалы и переписка с агентом</strong> — до удаления Заказчиком.
        Промежуточные рабочие копии удаляются автоматически по сроку, установленному тарифным
        планом;
      </li>
      <li>
        <strong>Сведения о платежах и закрывающие документы</strong> — пять лет, как того требует
        законодательство о бухгалтерском учёте;
      </li>
      <li>
        <strong>Маркеры доступа к внешней CRM</strong> — до отключения интеграции; при отключении
        стираются немедленно;
      </li>
      <li><strong>Журналы работы Сервиса</strong> — не более двенадцати месяцев.</li>
    </ul>
    <p>
      9.2. По достижении целей обработки, при отзыве согласия или по требованию субъекта данные
      удаляются или обезличиваются в срок, не превышающий тридцати дней, за исключением сведений,
      которые Оператор обязан хранить по закону.
    </p>

    <h2 id="cookies">10. Файлы cookie</h2>
    <p>
      10.1. Сервис использует только технически необходимые файлы cookie: они хранят
      идентификатор сессии, то есть удерживают вход. Cookie сессии недоступны сценариям страницы и
      передаются только по защищённому соединению.
    </p>
    <p>
      10.2. На страницах Сервиса размещён счётчик <strong>Яндекс.Метрики</strong> (оператор —
      ООО «ЯНДЕКС», Российская Федерация). Он устанавливает свои файлы cookie и передаёт
      Яндексу сведения о посещении: IP-адрес, данные браузера и устройства, просмотренные
      страницы, источник перехода и метки рекламной кампании. Цель — измерять посещаемость,
      понимать, какие страницы работают, и оценивать отдачу от рекламы. Обработка на стороне
      Яндекса регулируется его собственными документами, размещёнными на
      <a href="https://yandex.ru/legal/confidential/" rel="nofollow noopener" target="_blank"
        >yandex.ru/legal</a
      >.
    </p>
    <p>
      10.3. На публичных страницах Сервиса счётчик дополнительно записывает действия посетителя
      (движение указателя, прокрутка, клики) — это нужно, чтобы видеть, где страница непонятна.
      В личном кабинете такая запись <strong>не ведётся</strong>: там на экране содержимое
      документов Заказчика, и передавать его третьему лицу Оператор не вправе.
    </p>
    <p>
      10.4. На страницах документов, открываемых Клиентами Заказчика по публичной ссылке,
      счётчики <strong>не размещены</strong>: посетитель таких страниц не состоит с Оператором
      в отношениях и согласия ему не давал.
    </p>
    <p>
      10.5. Отказаться от аналитических cookie можно средствами браузера — запретом cookie
      сторонних сайтов или установкой
      <a
        href="https://yandex.ru/support/metrica/general/opt-out.html"
        rel="nofollow noopener"
        target="_blank"
        >блокировщика Яндекс.Метрики</a
      >. На работу Сервиса это не влияет.
    </p>
    <p>
      10.6. Запрет технически необходимых cookie в браузере делает вход в Сервис невозможным —
      это единственное последствие отказа.
    </p>

    <h2 id="security">11. Защита данных</h2>
    <p>11.1. Оператор принимает меры, предусмотренные статьёй 19 Закона № 152-ФЗ, в том числе:</p>
    <ul>
      <li>передача данных между браузером и Сервисом только по защищённому соединению;</li>
      <li>
        хранение паролей в виде необратимого хеша, а маркеров доступа к внешним системам и ссылок
        на документы — в зашифрованном виде;
      </li>
      <li>
        разграничение доступа по ролям внутри организации Заказчика и ограничение доступа
        сотрудников Оператора к данным клиентов;
      </li>
      <li>
        изоляция опубликованных документов от данных Сервиса, отзыв публичной ссылки в один шаг;
      </li>
      <li>журналирование действий и контроль попыток неправомерного доступа.</li>
    </ul>
    <p>
      11.2. Ни одна мера не даёт абсолютной гарантии. Обнаружив признаки неправомерного доступа к
      учётной записи, сообщите об этом по адресу из раздела 14 — доступ будет заблокирован.
    </p>

    <h2 id="rights">12. Права субъекта персональных данных</h2>
    <p>12.1. Субъект вправе:</p>
    <ul>
      <li>получить сведения об обработке своих персональных данных (статья 14 Закона № 152-ФЗ);</li>
      <li>требовать уточнения, блокирования или уничтожения неполных, устаревших или неточных данных;</li>
      <li>отозвать согласие на обработку;</li>
      <li>обжаловать действия Оператора в Роскомнадзоре или в суде.</li>
    </ul>
    <p>
      12.2. Запрос направляется по адресу электронной почты из раздела 14 и должен содержать
      сведения, позволяющие установить личность обратившегося и его отношение к запрашиваемым
      данным. Ответ предоставляется в срок, не превышающий десяти рабочих дней с даты получения
      запроса; срок может быть продлён не более чем на пять рабочих дней с уведомлением о причинах.
    </p>
    <p>
      12.3. Часть прав реализуется без обращения к Оператору: имя и адрес почты правятся в профиле,
      материалы и документы удаляются в рабочей области, интеграция с CRM отключается в разделе
      «Интеграции», учётная запись и организация удаляются по обращению в поддержку.
    </p>
    <p>
      12.4. Если персональные данные субъекта попали в Сервис в составе материалов Заказчика,
      сделок его CRM или заявок с его документов, обращение направляется Заказчику как оператору
      этих данных. Получив такое обращение, Оператор передаст его Заказчику и по его поручению
      удалит данные.
    </p>

    <h2 id="withdrawal">13. Отзыв согласия</h2>
    <p>
      13.1. Согласие отзывается письмом на адрес из раздела 14. Отзыв согласия не прекращает
      обработку, которая необходима для исполнения договора, для соблюдения требований закона или
      для защиты прав Оператора при рассмотрении спора.
    </p>
    <p>
      13.2. После отзыва согласия и прекращения договора данные удаляются или обезличиваются в
      порядке раздела 9.
    </p>

    <h2 id="changes">14. Изменения Политики и контакты</h2>
    <p>
      14.1. Оператор вправе изменять Политику. Новая редакция публикуется на этой странице и
      вступает в силу со дня публикации; дата редакции указана в начале документа.
    </p>
    <p>
      14.2. Вопросы об обработке персональных данных, запросы и отзывы согласия направляются по
      адресам ниже.
    </p>
    <dl v-if="company">
      <dt>Оператор</dt>
      <dd>{{ company.fullName || company.name || '—' }}</dd>
      <dt>ИНН</dt>
      <dd>{{ company.inn || '—' }}</dd>
      <template v-if="company.ogrn">
        <dt>ОГРН</dt>
        <dd>{{ company.ogrn }}</dd>
      </template>
      <dt>Адрес</dt>
      <dd>{{ company.address || '—' }}</dd>
      <dt>Электронная почта</dt>
      <dd>{{ company.email || '—' }}</dd>
      <template v-if="company.phone">
        <dt>Телефон</dt>
        <dd>{{ company.phone }}</dd>
      </template>
    </dl>
    <p v-else class="text-gray-500">Реквизиты загружаются…</p>
  </LegalDocument>
</template>
